import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { Session } from "./models/Session";
import { LoadingStatus } from "./models/LoadingStatus";
import { SessionsState } from "./models/SessionsState";
import { PaginatedResult } from "./contracts/PaginatedResult";
import { MAX_DISPLAYED_SESSIONS } from "./constants/sessionConstants";
import {
  fetchSession,
  createSession,
  updateSession,
  addInviteeToSession,
  removeInviteeFromSession,
  fetchSessionsPaginated,
  fetchSessionsByDateRangePaginated,
  fetchUpcomingSessions,
  fetchRecentlyCompletedSessions,
} from "./slice-extensions/sessions-slice-extension";

/**
 * Initial state for the sessions slice
 */
const initialState: SessionsState = {
  entities: [],
  status: LoadingStatus.Idle,
  statusMessage: undefined,
  upcomingSessionsState: {
    upcomingSessions: [],
    loadingStatus: LoadingStatus.Idle,
    initializationStatus: "not-initialized",
  },
  recentlyCompletedSessionsState: {
    recentlyCompletedSessions: [],
    loadingStatus: LoadingStatus.Idle,
    initializationStatus: "not-initialized",
  },
  pagination: {
    page: 1,
    pageSize: 10,
    totalCount: 0,
    totalPages: 0,
    hasPreviousPage: false,
    hasNextPage: false,
  },
};

/**
 * Sessions slice containing reducers and actions
 */
const sessionsSlice = createSlice({
  name: "sessions",
  initialState,
  reducers: {
    /**
     * Clears any error messages from the state
     */
    clearError: (state) => {
      state.statusMessage = undefined;
      if (state.status === LoadingStatus.Failed) {
        state.status = LoadingStatus.Idle;
      }
    },
    /**
     * Resets the sessions state to initial state
     */
    resetSessions: () => initialState,
  },
  extraReducers: (builder) => {
    // Fetch session cases
    builder
      .addCase(fetchSession.pending, (state) => {
        state.status = LoadingStatus.Loading;
        state.statusMessage = undefined;
      })
      .addCase(
        fetchSession.fulfilled,
        (state, action: PayloadAction<Session>) => {
          state.status = LoadingStatus.Idle;
          state.statusMessage = undefined;
          const existingIndex = state.entities.findIndex(
            (session) => session.id === action.payload.id
          );
          if (existingIndex >= 0) {
            state.entities[existingIndex] = action.payload;
          } else {
            state.entities.push(action.payload);
          }
        }
      )
      .addCase(fetchSession.rejected, (state, action) => {
        state.status = LoadingStatus.Failed;
        state.statusMessage =
          (action.payload as string) || "Failed to fetch session";
      });

    // Create session cases
    builder
      .addCase(createSession.pending, (state) => {
        state.status = LoadingStatus.Loading;
        state.statusMessage = undefined;
      })
      .addCase(
        createSession.fulfilled,
        (state, action: PayloadAction<Session>) => {
          state.status = LoadingStatus.Idle;
          state.statusMessage = undefined;
          state.entities.push(action.payload);
          
          const newSession = action.payload;
          const now = new Date();
          const sessionStartTime = new Date(newSession.dateStart);
          
          if (sessionStartTime > now) {
            const currentUpcomingSessions = [...state.upcomingSessionsState.upcomingSessions];
            
            const existingIndex = currentUpcomingSessions.findIndex(
              (session) => session.id === newSession.id
            );
            
            if (existingIndex === -1) {
              currentUpcomingSessions.push(newSession);
              
              currentUpcomingSessions.sort(
                (a, b) => new Date(a.dateStart).getTime() - new Date(b.dateStart).getTime()
              );
              
              if (state.upcomingSessionsState.upcomingSessions.length > 0) {
                const firstSessionTime = new Date(currentUpcomingSessions[0].dateStart);
                const lastSessionTime = new Date(currentUpcomingSessions[currentUpcomingSessions.length - 1].dateStart);
                
              
                const sessionFitsInRange = sessionStartTime >= firstSessionTime && sessionStartTime <= lastSessionTime;
                
                if (sessionFitsInRange || currentUpcomingSessions.length <= MAX_DISPLAYED_SESSIONS) {
                  state.upcomingSessionsState.upcomingSessions = currentUpcomingSessions.slice(0, MAX_DISPLAYED_SESSIONS);
                }
              } else {
                state.upcomingSessionsState.upcomingSessions = currentUpcomingSessions.slice(0, MAX_DISPLAYED_SESSIONS);
              }
            }
          }
        }
      )
      .addCase(createSession.rejected, (state, action) => {
        state.status = LoadingStatus.Failed;
        state.statusMessage =
          (action.payload as string) || "Failed to create session";
      });

    // Update session cases
    builder
      .addCase(updateSession.pending, (state) => {
        state.status = LoadingStatus.Loading;
        state.statusMessage = undefined;
      })
      .addCase(
        updateSession.fulfilled,
        (state, action: PayloadAction<Session>) => {
          state.status = LoadingStatus.Idle;
          state.statusMessage = undefined;
          const index = state.entities.findIndex(
            (session) => session.id === action.payload.id
          );
          if (index >= 0) {
            state.entities[index] = action.payload;
          }
        }
      )
      .addCase(updateSession.rejected, (state, action) => {
        state.status = LoadingStatus.Failed;
        state.statusMessage =
          (action.payload as string) || "Failed to update session";
      });

    // Add invitee to session cases
    builder
      .addCase(addInviteeToSession.pending, (state) => {
        state.status = LoadingStatus.Loading;
        state.statusMessage = undefined;
      })
      .addCase(
        addInviteeToSession.fulfilled,
        (state, action: PayloadAction<Session>) => {
          state.status = LoadingStatus.Idle;
          state.statusMessage = undefined;
          const index = state.entities.findIndex(
            (session) => session.id === action.payload.id
          );
          if (index >= 0) {
            state.entities[index] = action.payload;
          }
        }
      )
      .addCase(addInviteeToSession.rejected, (state, action) => {
        state.status = LoadingStatus.Failed;
        state.statusMessage =
          (action.payload as string) || "Failed to add invitee to session";
      });

    // Remove invitee from session cases
    builder
      .addCase(removeInviteeFromSession.pending, (state) => {
        state.status = LoadingStatus.Loading;
        state.statusMessage = undefined;
      })
      .addCase(
        removeInviteeFromSession.fulfilled,
        (state, action: PayloadAction<Session>) => {
          state.status = LoadingStatus.Idle;
          state.statusMessage = undefined;
          const index = state.entities.findIndex(
            (session) => session.id === action.payload.id
          );
          if (index >= 0) {
            state.entities[index] = action.payload;
          }
        }
      )
      .addCase(removeInviteeFromSession.rejected, (state, action) => {
        state.status = LoadingStatus.Failed;
        state.statusMessage =
          (action.payload as string) || "Failed to remove invitee from session";
      });

    // Fetch sessions paginated cases
    builder
      .addCase(fetchSessionsPaginated.pending, (state) => {
        state.status = LoadingStatus.Loading;
        state.statusMessage = undefined;
      })
      .addCase(
        fetchSessionsPaginated.fulfilled,
        (state, action: PayloadAction<PaginatedResult<Session>>) => {
          state.status = LoadingStatus.Idle;
          state.statusMessage = undefined;
          state.entities = action.payload.items;
          state.pagination = {
            page: action.payload.page,
            pageSize: action.payload.pageSize,
            totalCount: action.payload.totalCount,
            totalPages: action.payload.totalPages,
            hasPreviousPage: action.payload.hasPreviousPage,
            hasNextPage: action.payload.hasNextPage,
          };
        }
      )
      .addCase(fetchSessionsPaginated.rejected, (state, action) => {
        state.status = LoadingStatus.Failed;
        state.statusMessage =
          (action.payload as string) || "Failed to fetch sessions";
      });

    // Fetch sessions by date range paginated cases
    builder
      .addCase(fetchSessionsByDateRangePaginated.pending, (state) => {
        state.status = LoadingStatus.Loading;
        state.statusMessage = undefined;
      })
      .addCase(
        fetchSessionsByDateRangePaginated.fulfilled,
        (state, action: PayloadAction<PaginatedResult<Session>>) => {
          state.status = LoadingStatus.Idle;
          state.statusMessage = undefined;
          state.entities = action.payload.items;
          state.pagination = {
            page: action.payload.page,
            pageSize: action.payload.pageSize,
            totalCount: action.payload.totalCount,
            totalPages: action.payload.totalPages,
            hasPreviousPage: action.payload.hasPreviousPage,
            hasNextPage: action.payload.hasNextPage,
          };
        }
      )
      .addCase(fetchSessionsByDateRangePaginated.rejected, (state, action) => {
        state.status = LoadingStatus.Failed;
        state.statusMessage =
          (action.payload as string) ||
          "Failed to fetch sessions by date range";
      });

    // Fetch upcoming sessions cases
    builder
      .addCase(fetchUpcomingSessions.pending, (state) => {
        state.upcomingSessionsState.loadingStatus = LoadingStatus.Loading;
        state.upcomingSessionsState.initializationStatus = "initializing";
        state.statusMessage = undefined;
      })
      .addCase(
        fetchUpcomingSessions.fulfilled,
        (state, action: PayloadAction<Session[]>) => {
          state.upcomingSessionsState.loadingStatus = LoadingStatus.Idle;
          state.upcomingSessionsState.initializationStatus = "initialized";
          state.statusMessage = undefined;
          
          // Replace all upcoming sessions with the new data to ensure freshness
          state.upcomingSessionsState.upcomingSessions = action.payload;
        }
      )
      .addCase(fetchUpcomingSessions.rejected, (state, action) => {
        state.upcomingSessionsState.loadingStatus = LoadingStatus.Failed;
        state.upcomingSessionsState.initializationStatus = "initialized";
        state.statusMessage =
          (action.payload as string) || "Failed to fetch upcoming sessions";
      });

    // Fetch recently completed sessions cases
    builder
      .addCase(fetchRecentlyCompletedSessions.pending, (state) => {
        state.recentlyCompletedSessionsState.loadingStatus = LoadingStatus.Loading;
        state.recentlyCompletedSessionsState.initializationStatus = "initializing";
        state.statusMessage = undefined;
      })
      .addCase(
        fetchRecentlyCompletedSessions.fulfilled,
        (state, action: PayloadAction<Session[]>) => {
          state.recentlyCompletedSessionsState.loadingStatus = LoadingStatus.Idle;
          state.recentlyCompletedSessionsState.initializationStatus = "initialized";
          state.statusMessage = undefined;
          
          // Replace all recently completed sessions with the new data to ensure freshness
          state.recentlyCompletedSessionsState.recentlyCompletedSessions = action.payload;
        }
      )
      .addCase(fetchRecentlyCompletedSessions.rejected, (state, action) => {
        state.recentlyCompletedSessionsState.loadingStatus = LoadingStatus.Failed;
        state.recentlyCompletedSessionsState.initializationStatus = "initialized";
        state.statusMessage =
          (action.payload as string) || "Failed to fetch recently completed sessions";
      });
  },
});

export const { actions, reducer } = sessionsSlice;
export const { clearError, resetSessions } = actions;
export default reducer;
